import React from "react";
import {
  Box,
  Grid,
  Card,
  CardActionArea,
  CardMedia,
  CardContent,
  Typography,
  Chip,
  Stack,
  useTheme,

} from "@mui/material";

import CabinetDialog from "../../Utilities/CabinetDialog.js"
import cabinetView from "../../../static/images/TSDD/cabinets/tsdd_cab_view.webp"

import { CABINETS } from "./Data/CabinetData";



// Shared chip style for the “Rock / Crunch / …” bubbles
const tagChipSx = {
  borderRadius: 999,
  px: 1.4,
  height: 24,
  fontSize: "0.7rem",
  textTransform: "none",
  borderColor: "rgba(255,255,255,0.35)",
  bgcolor: "rgba(255,255,255,0.06)",
};

// ---- Main overview -----------------------------------------------------

const CabinetsOverview = () => {
  const theme = useTheme();
  const [selectedIndex, setSelectedIndex] = React.useState(null);
  const [dialogOpen, setDialogOpen] = React.useState(false);

  const handleOpen = (index) => {
    setSelectedIndex(index);
    setDialogOpen(true);
  };

  const handleClose = () => {
    setDialogOpen(false);
  };

  const handlePrevCabinet = () => {
    setSelectedIndex((prev) => {
      const last = CABINETS.length - 1;
      if (prev === null) return 0;
      return prev === 0 ? last : prev - 1;
    });
  };

  const handleNextCabinet = () => {
    setSelectedIndex((prev) => {
      const last = CABINETS.length - 1;
      if (prev === null) return 0;
      return prev === last ? 0 : prev + 1;
    });
  };

  const selectedCabinet =
    selectedIndex !== null ? CABINETS[selectedIndex] : null;

  const canCycle = CABINETS.length > 1;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100%",
        bgcolor: "#1a1a1a",
        color: theme.palette.text.primary,
        display: "flex",
        flexDirection: "column",
        py: { xs: 4, md: 6 },
        px: { xs: 2, md: 4, lg: 6 },
      }}
    >
      {/* Section header */}
      <Box sx={{ mb: 4, textAlign: "center" }}>
        <Typography variant="overline" sx={{ opacity: 0.7 }}>
          Cabinets
        </Typography>
        <Typography
          variant="h4"
          component="h2"
          sx={{ fontWeight: 700, mt: 1 }}
        >
          Four essential cabinets. Endless tones.
        </Typography>
        <Typography
          variant="body1"
          sx={{
            mt: 1.5,
            maxWidth: 640,
            mx: "auto",
            opacity: 0.85,
          }}
        >
          Each cabinet is carefully captured with a comprehensive collection of
          high end studio grade microphones, giving you a focused toolkit that
          covers everything from tight modern metal to warm vintage cleans.
        </Typography>

        <Typography
          variant="body1"
          sx={{
            mt: 1.5,
            maxWidth: 640,
            mx: "auto",
            opacity: 0.85,
          }}
        >
          Explore below what each cabinet has to offer.
        </Typography>
      </Box>

      {/* Cab grid */}
      <Box sx={{ flexGrow: 0.3, justifyContent: "center" }}>
        <Grid container spacing={3} justifyContent={"center"}>
          {CABINETS.map((cab, index) => (
            <Grid item xs={12} sm={6} md={3} key={cab.id}>
              <Card
                variant="outlined"
                sx={{
                  position: "relative",
                  borderRadius: 3,
                  height: "100%",
                  bgcolor: "rgba(24, 24, 28, 0.98)",
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                  border: "1px solid rgba(255,255,255,0.06)",
                  boxShadow:
                    "0 18px 45px rgba(0,0,0,0.85), 0 0 0 1px rgba(255,255,255,0.02)",
                  transition:
                    "transform 0.22s ease-out, box-shadow 0.22s ease-out, border-color 0.22s ease-out, background 0.22s ease-out",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    inset: 0,
                    pointerEvents: "none",
                    background:
                      "radial-gradient(circle at 0% 0%, rgba(255,255,255,0.18), transparent 60%)",
                    opacity: 0.8,
                  },
                  "&:hover": {
                    borderColor: "rgba(255,255,255,0.9)",
                    boxShadow:
                      "0 26px 70px rgba(0,0,0,0.95), 0 0 0 1px rgba(255,255,255,0.55)",
                    transform: "translateY(-6px) scale(1.015)",
                    background:
                      "linear-gradient(145deg, rgba(28,28,32,1), rgba(38,38,44,1))",
                  },
                }}
              >
                <CardActionArea
                  onClick={() => handleOpen(index)}
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "stretch",
                    position: "relative",
                    zIndex: 1,
                  }}
                >
                  {/* Cab artwork */}
                  <CardMedia
                    component="img"
                    image={cab.image}
                    alt={cab.name}
                    sx={{
                      aspectRatio: "4 / 3",
                      objectFit: "cover",
                      width: "100%",
                    }}
                  />

                  {/* Text content */}
                  <CardContent sx={{ flexGrow: 1, width: "100%" }}>
                    <Typography
                      variant="caption"
                      sx={{
                        opacity: 0.7,
                        mb: 0.3,
                        textTransform: "uppercase",
                        letterSpacing: 1,
                        color: theme.palette.text.secondary,
                      }}
                    >
                      Cabinet
                    </Typography>
                    {/* main title smaller */}
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 600,
                        fontSize: "0.95rem",
                      }}
                    >
                      {cab.name}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: "0.875rem !important",
                        mt: 0.5,
                        mb: 1,
                        opacity: 0.85,
                        color: theme.palette.text.secondary,
                      }}
                    >
                      {cab.subtitle}
                    </Typography>

                    {/* Tag chips on the tiles – pill-shaped bubbles */}
                    {cab.tags && cab.tags.length > 0 && (
                      <Stack
                        direction="row"
                        spacing={0.75}
                        flexWrap="wrap"
                        sx={{ mb: 1 }}
                      >
                        {cab.tags.map((tag) => (
                          <Chip
                            key={tag}
                            size="small"
                            label={tag}
                            variant="outlined"
                            sx={tagChipSx}
                          />
                        ))}
                      </Stack>
                    )}

                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        mt: 0.5,
                        opacity: 0.7,
                        color: theme.palette.text.secondary,
                      }}
                    >
                      Click to explore microphones
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
      <Box sx={{ mb: 4, mt: 6, textAlign: "center" }}>
        <Typography
          variant="h4"
          component="h2"
          sx={{ fontWeight: 700, mt: 1 }}
        >
          Craft your own sound.
        </Typography>

        <Typography
          variant="body1"
          sx={{
            mt: 1.5,
            maxWidth: 640,
            mx: "auto",
            opacity: 0.85,
          }}
        >
          With <i>over 2500</i> individual IRs, the cabinet section lets you
          pinpoint exactly where you want to put your virtual microphone. You
          can even choose to blend mics together, or split the view and mic two
          cabinets at once.
        </Typography>

        <img loading="lazy" alt="Guitar Amplifier" src={cabinetView} width="100%" style={{ maxWidth: "1000px" }}></img>
      </Box>

      {/* Popup */}
      <CabinetDialog
        open={dialogOpen}
        cabinet={selectedCabinet}
        onClose={handleClose}
        onPrevCabinet={handlePrevCabinet}
        onNextCabinet={handleNextCabinet}
        canCycle={canCycle}
      />
    </Box>
  );
};

export default CabinetsOverview;
